import { Cause, Effect, Inspectable, Option, Pipeable, Schema, Scope, Stream } from 'effect';
import {
  HttpClient,
  HttpClientError,
  HttpClientResponse,
  HttpIncomingMessage,
  UrlParams,
  type HttpClientRequest,
} from 'effect/unstable/http';

const discard = (response: Response) => {
  try {
    return response.body?.cancel().catch(() => undefined) ?? Promise.resolve();
  } catch {
    return Promise.resolve();
  }
};

/** A response reader belongs to the native consumer, including interrupted reads. */
class OwnedResponse extends Inspectable.Class implements HttpClientResponse.HttpClientResponse {
  readonly [HttpClientResponse.TypeId] = HttpClientResponse.TypeId;
  readonly [HttpIncomingMessage.TypeId] = HttpIncomingMessage.TypeId;
  private readonly metadata: HttpClientResponse.HttpClientResponse;
  private bytes: ArrayBuffer | undefined;

  constructor(
    readonly request: HttpClientRequest.HttpClientRequest,
    private readonly source: Response,
    private readonly abort: () => void,
  ) {
    super();
    this.metadata = HttpClientResponse.fromWeb(request, source);
  }

  get status() {
    return this.source.status;
  }
  get headers() {
    return this.metadata.headers;
  }
  get cookies() {
    return this.metadata.cookies;
  }
  get remoteAddress() {
    return Option.none<string>();
  }
  private decodeFailure = (cause: unknown): HttpClientError.HttpClientError =>
    new HttpClientError.HttpClientError({
      reason: new HttpClientError.DecodeError({ request: this.request, response: this, cause }),
    });

  get stream(): Stream.Stream<Uint8Array, HttpClientError.HttpClientError> {
    const response = this;
    return Stream.fromPull(
      Effect.gen(function* () {
        if (!response.source.body) return Cause.done();
        const reader = yield* Effect.acquireRelease(
          Effect.try({
            try: () => response.source.body!.getReader(),
            catch: response.decodeFailure,
          }),
          (reader) =>
            Effect.sync(response.abort).pipe(
              Effect.andThen(
                Effect.tryPromise({
                  try: () => reader.cancel(),
                  catch: response.decodeFailure,
                }).pipe(Effect.ignore),
              ),
              Effect.ensuring(Effect.sync(() => reader.releaseLock())),
            ),
        );
        return Effect.tryPromise({ try: () => reader.read(), catch: response.decodeFailure }).pipe(
          Effect.flatMap((part) =>
            part.done ? Cause.done() : Effect.succeed([part.value] as const),
          ),
        );
      }),
    ).pipe(Stream.scoped);
  }

  get arrayBuffer(): Effect.Effect<ArrayBuffer, HttpClientError.HttpClientError> {
    const response = this;
    return Effect.gen(function* () {
      if (response.bytes !== undefined) return response.bytes;
      const chunks = yield* Stream.runCollect(response.stream);
      const bytes = new Uint8Array(chunks.reduce((length, chunk) => length + chunk.length, 0));
      let offset = 0;
      for (const chunk of chunks) {
        bytes.set(chunk, offset);
        offset += chunk.length;
      }
      response.bytes = bytes.buffer;
      return response.bytes;
    });
  }
  get text() {
    return Effect.map(this.arrayBuffer, (bytes) => new TextDecoder().decode(bytes));
  }
  get json(): Effect.Effect<Schema.Json, HttpClientError.HttpClientError> {
    return Effect.flatMap(this.text, (text) =>
      Effect.try({ try: () => (text === '' ? null : JSON.parse(text)), catch: this.decodeFailure }),
    );
  }
  get urlParamsBody() {
    return Effect.map(this.text, (text) => UrlParams.fromInput(new URLSearchParams(text)));
  }
  get formData(): Effect.Effect<FormData, HttpClientError.HttpClientError> {
    return Effect.flatMap(this.arrayBuffer, (bytes) =>
      Effect.tryPromise({
        try: () => new Response(bytes, { headers: this.source.headers }).formData(),
        catch: this.decodeFailure,
      }),
    );
  }
  toJSON() {
    return this.metadata.toJSON();
  }
  pipe() {
    return Pipeable.pipeArguments(this, arguments);
  }
}

/** The generated core client keeps each request and unread response in its operation scope. */
export function makeScopedHttpClient(fetch: typeof globalThis.fetch, scope: Scope.Scope) {
  return HttpClient.make((request, url, signal) =>
    Effect.gen(function* () {
      const state = yield* Effect.acquireRelease(
        Effect.sync(() => ({
          controller: new AbortController(),
          closed: false,
          response: undefined as Response | undefined,
        })),
        (state) =>
          Effect.gen(function* () {
            state.closed = true;
            state.controller.abort();
            if (state.response) yield* Effect.promise(() => discard(state.response!));
          }),
      ).pipe(Scope.provide(scope));
      if (state.closed || signal.aborted) return yield* Effect.interrupt;
      const headers = { ...request.headers };
      delete headers['content-length'];
      const body =
        request.body._tag === 'Stream'
          ? yield* Stream.toReadableStreamEffect(request.body.stream).pipe(Scope.provide(scope))
          : request.body._tag === 'FormData'
            ? request.body.formData
            : request.body._tag === 'Empty'
              ? undefined
              : request.body.body;
      const failure = (cause: unknown) =>
        new HttpClientError.HttpClientError({
          reason: new HttpClientError.TransportError({ request, cause }),
        });
      const response = yield* Effect.callback<Response, HttpClientError.HttpClientError>(
        (resume) => {
          try {
            const pending = fetch(url, {
              method: request.method,
              headers,
              body: body as BodyInit | undefined,
              ...(request.body._tag === 'Stream' ? { duplex: 'half' } : {}),
              signal: AbortSignal.any([signal, state.controller.signal]),
            });
            void pending.then(
              (response) => {
                if (state.closed) {
                  void discard(response);
                  return;
                }
                state.response = response;
                resume(Effect.succeed(response));
              },
              (cause) => resume(Effect.fail(failure(cause))),
            );
          } catch (cause) {
            resume(Effect.fail(failure(cause)));
          }
        },
      );
      return new OwnedResponse(request, response, () => state.controller.abort());
    }),
  );
}
